import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { observer } from '@legendapp/state/react';
import { auth$, logout } from '@/stores/auth$';
import { ui$, setViewMode, setTheme, setSelectedTagFilter, setMobileSidebarOpen, navigateOnMobile } from '@/stores/ui$';
import { cn } from '@/lib/utils';
import { FileText, Trash2, Hash, Archive, Heart, Sun, Moon, Monitor, User, LogOut, Plug, Settings, Sparkles, Command, Zap, Smartphone, ExternalLink, Puzzle } from 'lucide-react';
import SyncStatus from './SyncStatus';
import TagTree from './TagTree';
import SharedWithMe from './SharedWithMe';

// Lazy load modals — only loaded when user opens them
const TagShareModal = lazy(() => import('./TagShareModal'));
const MCPModal = lazy(() => import('./MCPModal'));
const SettingsModal = lazy(() => import('./SettingsModal'));
const OnboardingModal = lazy(() => import('./OnboardingModal'));

const Sidebar = observer(function Sidebar() {
  const viewMode = ui$.viewMode.get();
  const theme = ui$.theme.get();
  const user = auth$.user.get();
  const selectedTagFilter = ui$.selectedTagFilter.get();
  const isMobileSidebarOpen = ui$.isMobileSidebarOpen.get();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [shareTagPath, setShareTagPath] = useState(null);
  const [showMCPModal, setShowMCPModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState(undefined);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle ?action= deeplinks
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action === 'set-password') {
      setSettingsInitialTab('account');
      setShowSettingsModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (action === 'mcp-access') {
      setShowMCPModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Update dropdown position when it opens
  useEffect(() => {
    if (showUserMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 8, // 8px above the button
        left: rect.left,
        width: 200, // Fixed width for dropdown
      });
    }
  }, [showUserMenu]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showUserMenu) return;

    const handleClickOutside = (e) => {
      // Check if click is outside both button and dropdown
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const navItems = [
    { id: 'notes', icon: FileText, label: 'Notes' },
    { id: 'skills', icon: Puzzle, label: 'Skills' },
    { id: 'favorites', icon: Heart, label: 'Favorites' },
    { id: 'archive', icon: Archive, label: 'Archived' },
    { id: 'trash', icon: Trash2, label: 'Trash' },
  ];

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobileSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "w-[240px] md:w-[240px] border-r border-border h-full min-h-0 flex flex-col transition-transform duration-300",
        // Background: solid on mobile (overlay), semi-transparent on desktop
        "bg-background md:bg-muted/30",
        // Mobile: fixed overlay, slide in from left, with safe-area-aware top/bottom padding
        "fixed top-0 md:relative md:top-auto z-50 md:z-auto pt-[var(--mobile-header-h)] md:pt-0 pb-[var(--sab)] md:pb-0",
        isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        <div className="hidden md:block p-4 md:p-6">
             <h1
               onClick={() => {
                 setViewMode('notes');
                 setSelectedTagFilter(null);
               }}
               className="font-bold text-xl tracking-tight cursor-pointer hover:opacity-80 transition-opacity"
             >
               Prompt<span className="text-primary">Note</span>
             </h1>
        </div>

        <nav className="flex-1 min-h-0 px-2 md:px-4 space-y-1 overflow-y-auto overscroll-contain mt-4">
            <div className="mb-4">
                <p className="hidden md:block px-2 text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Views</p>
                {navItems.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => {
                          setViewMode(item.id);
                          setSelectedTagFilter(null);
                          navigateOnMobile();
                        }}
                        className={cn(
                            "w-full flex items-center gap-3 px-2 md:px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-accent",
                            viewMode === item.id && !selectedTagFilter ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        <item.icon size={18} />
                        <span>{item.label}</span>
                    </button>
                ))}
            </div>

            <div className="mt-8">
                 {/* Show TagTree on both mobile drawer and desktop */}
                 <TagTree
                   onShareTag={(tagPath) => setShareTagPath(tagPath)}
                   onNavigate={navigateOnMobile}
                   onOpenSettings={(tab) => {
                     setSettingsInitialTab(tab);
                     setShowSettingsModal(true);
                   }}
                 />
                 <SharedWithMe />
            </div>
        </nav>
        
        {/* User section - desktop only (mobile has top bar) */}
        <div className="hidden md:block p-3 border-t border-border mt-auto">
             <div className="flex items-center justify-between gap-2">
                {/* User Profile */}
                <div className="flex-1 min-w-0">
                    <button
                        ref={buttonRef}
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors focus:outline-none"
                        title="User menu"
                    >
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                            <User size={14} />
                        </div>
                    </button>

                    {/* Dropdown Menu - Rendered via Portal */}
                    {showUserMenu && createPortal(
                      <div
                        ref={dropdownRef}
                        className="fixed bg-popover border border-border rounded-lg shadow-lg py-1 z-dropdown animate-in fade-in slide-in-from-bottom-2 duration-100"
                        style={{
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                          transform: 'translateY(-100%)',
                        }}
                      >
                        {/* User Info Header */}
                        <div className="px-3 py-2 border-b border-border/50">
                          <p className="text-xs font-medium truncate">
                            {user?.username || user?.email || 'User'}
                          </p>
                          <p className="text-[10px] text-muted-foreground truncate">
                            {user?.email}
                          </p>
                        </div>

                        {/* Menu Items */}
                        <button
                          onClick={() => {
                            setShowSettingsModal(true);
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 transition-colors focus:outline-none"
                        >
                          <Settings size={14} />
                          <span>Settings</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowMCPModal(true);
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 transition-colors focus:outline-none"
                        >
                          <Plug size={14} />
                          <span>MCP / API Access</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowOnboarding(true);
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 transition-colors focus:outline-none"
                        >
                          <Sparkles size={14} />
                          <span>Welcome Tour</span>
                        </button>

                        {/* Ecosystem links */}
                        <div className="border-t border-border/50 mt-1 pt-1">
                          <p className="px-3 pt-1 pb-0.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">Use Everywhere</p>
                          <a
                            href="https://www.raycast.com/dimpurr/promptnote"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2 transition-colors"
                          >
                            <Command size={14} />
                            <span className="flex-1">Raycast</span>
                            <ExternalLink size={10} className="text-muted-foreground/50" />
                          </a>
                          <a
                            href="https://promptnoteapp.com/docs/skills/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2 transition-colors"
                          >
                            <Zap size={14} />
                            <span className="flex-1">Agent Skills</span>
                            <ExternalLink size={10} className="text-muted-foreground/50" />
                          </a>
                          <a
                            href="https://promptnoteapp.com/docs/android/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2 transition-colors"
                          >
                            <Smartphone size={14} />
                            <span className="flex-1">Android App</span>
                            <ExternalLink size={10} className="text-muted-foreground/50" />
                          </a>
                        </div>

                        <div className="border-t border-border/50 mt-1 pt-1">
                          <button
                            onClick={() => {
                              handleLogout();
                              setShowUserMenu(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2 transition-colors focus:outline-none"
                          >
                            <LogOut size={14} />
                            <span>Log out</span>
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}
                </div>

                {/* Sync Status & Theme Toggles */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Sync Status */}
                  <SyncStatus />

                  {/* Theme Toggles */}
                  <div className="hidden md:flex items-center gap-0.5 bg-muted/50 p-0.5 rounded-lg">
                    <button
                      onClick={() => setTheme('light')}
                      className={cn(
                        "p-1.5 rounded-md transition-all",
                        theme === 'light' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground/60 hover:bg-background hover:text-foreground hover:shadow-sm'
                      )}
                      title="Light"
                    >
                      <Sun size={12} />
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={cn(
                        "p-1.5 rounded-md transition-all",
                        theme === 'dark' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground/60 hover:bg-background hover:text-foreground hover:shadow-sm'
                      )}
                      title="Dark"
                    >
                      <Moon size={12} />
                    </button>
                    <button
                      onClick={() => setTheme('system')}
                      className={cn(
                        "p-1.5 rounded-md transition-all",
                        theme === 'system' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground/60 hover:bg-background hover:text-foreground hover:shadow-sm'
                      )}
                      title="System"
                    >
                      <Monitor size={12} />
                    </button>
                  </div>
                </div>
             </div>
        </div>
      </div>

      {/* Lazy-loaded modals — Suspense fallback=null since modals have their own open/close transitions */}
      <Suspense fallback={null}>
        {!!shareTagPath && (
          <TagShareModal
            tagPath={shareTagPath}
            isOpen={!!shareTagPath}
            onClose={() => setShareTagPath(null)}
          />
        )}

        {showMCPModal && (
          <MCPModal
            isOpen={showMCPModal}
            onClose={() => setShowMCPModal(false)}
          />
        )}

        {showSettingsModal && (
          <SettingsModal
            isOpen={showSettingsModal}
            initialTab={settingsInitialTab}
            onClose={() => setShowSettingsModal(false)}
          />
        )}

        {showOnboarding && (
          <OnboardingModal
            isOpen={showOnboarding}
            onClose={() => setShowOnboarding(false)}
          />
        )}
      </Suspense>
    </>
  );
});

export default Sidebar;
